import { Box } from '@material-ui/core'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Text } from 'shared/components/Text/Text'
import { darkGray, dividerGray, lightGray1, midGray, primaryBlack } from 'shared/theme/colors'
import styled, { css } from 'styled-components'

import { formatReadableDate, formatReadableTime } from '../../../helpers/dateHelpers'
import { formatCurrency } from '../../../helpers/formatters'
import {
  getPaymentPeriodsFromPolicy,
  Repayment,
  RepaymentPolicyStatus,
  RepaymentScheduleType,
  RepaymentScheduleTypeText,
  VendorProgramPolicyType,
} from '../../../models/repayments'
import { Divider } from '../../Base/Divider'
import { Grid } from '../../Base/Grid'
import { InfoTooltip } from '../../InfoTooltip/InfoTooltip'
import { getPolicyHeader, getPolicyStatus } from '../Policy.helper'

interface PolicyDetailBillingProps {
  policy: Repayment
}

export function PolicyDetailBilling({ policy }: PolicyDetailBillingProps) {
  const policyHeader = getPolicyHeader(policy)
  const status = getPolicyStatus(policy)

  const currentPeriodRange = `${formatReadableDate(policy.repayment_details.current_period_start_date, {
    monthShort: true,
  })} - ${formatReadableDate(policy.repayment_details.current_period_end_date, { monthShort: true })}`
  const schedule = policy.repayment_details.payment_period_type ?? RepaymentScheduleType.WEEKLY
  const paymentPeriods = getPaymentPeriodsFromPolicy(policy)
  const amountOwedPerPeriod =
    policy.repayment_details.amount_owed_per_period === 'N/A'
      ? 'N/A'
      : parseFloat(policy.repayment_details.amount_owed_per_period)
  const debt_written_off = policy.repayment_details.total_debt_written_off ?? '0.00'

  return (
    <Grid columns="1fr 1fr 1fr" smColumns="1fr 1fr" xsColumns="1fr" gap="60px">
      {status !== RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION && (
        <div>
          <Text variant="uppercaseLabel2" color={midGray} mb={2}>
            Current billing period
          </Text>

          {status === RepaymentPolicyStatus.FINISHED || status === RepaymentPolicyStatus.TERMINATED ? (
            <Text variant="header3" color={lightGray1}>
              Policy was finished on {formatReadableDate(policy.repayment_details.payment_end_date)}{' '}
              {formatReadableTime(policy.repayment_details.payment_end_date)}
            </Text>
          ) : status === RepaymentPolicyStatus.DRAFT ? (
            <Text variant="header3" color={lightGray1}>
              Collection hasn't started
            </Text>
          ) : (
            <>
              <Grid columns="1fr auto" gap="10px" styles="width: 100%">
                <Text variant="header3" color={lightGray1}>
                  {currentPeriodRange}
                </Text>
                <Text variant="header3" color={primaryBlack} align="right">
                  {policy.repayment_details.amount_owed_this_period === 'N/A'
                    ? 'N/A'
                    : formatCurrency(policy.repayment_details.amount_owed_this_period)}
                </Text>

                <InsetText variant="label2" color={lightGray1}>
                  {schedule === RepaymentScheduleType.MONTHLY ? 'Monthly' : 'Weekly'} deduction
                </InsetText>
                <Text variant="header3" color={primaryBlack} align="right">
                  {policy.repayment_details.amount_owed_per_period === 'N/A'
                    ? 'N/A'
                    : formatCurrency(policy.repayment_details.amount_owed_per_period)}
                </Text>

                <InsetText variant="label2" color={lightGray1} $isLast>
                  Overdue balance
                  <InfoTooltip zIndex={10}>
                    <Text variant="paragraph2" fontColor={darkGray}>
                      This is the underpayment amount from the previous collection period. The total of{' '}
                      {schedule === RepaymentScheduleType.MONTHLY ? 'monthly' : 'weekly'} payment and the
                      underpayment is due for {policyHeader} during {currentPeriodRange}.
                    </Text>
                  </InfoTooltip>
                </InsetText>
                <Text variant="header3" color={primaryBlack} align="right">
                  {policy.repayment_details.underpayment_from_previous_periods === 'N/A'
                    ? 'N/A'
                    : formatCurrency(policy.repayment_details.underpayment_from_previous_periods)}
                </Text>

                <Text variant="header3" color={lightGray1} mt={1}>
                  Amount paid this {schedule === RepaymentScheduleType.MONTHLY ? 'month' : 'week'}
                </Text>
                <Text variant="header3" color={primaryBlack} mt={1} align="right">
                  {policy.repayment_details.amount_paid_this_period === 'N/A'
                    ? 'N/A'
                    : formatCurrency(policy.repayment_details.amount_paid_this_period)}
                </Text>
              </Grid>

              <Box mt={1} />
              <Grid
                columns="1fr auto"
                styles={`background-color: #e8fbff; padding: 10px; width: calc(100% + 20px); margin: 0 -10px;`}
              >
                <Text variant="header3" color={lightGray1}>
                  Current balance
                </Text>
                <Text variant="header3" color={primaryBlack} align="right">
                  {policy.repayment_details.remaining_payment_this_period === 'N/A'
                    ? 'N/A'
                    : formatCurrency(policy.repayment_details.remaining_payment_this_period)}
                </Text>
              </Grid>
            </>
          )}
        </div>
      )}

      <div>
        {status !== RepaymentPolicyStatus.FINISHED &&
          status !== RepaymentPolicyStatus.TERMINATED &&
          status !== RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION && (
            <>
              <Text variant="uppercaseLabel2" color={midGray} mb={2}>
                Next Billing Period
              </Text>
              <Grid
                columns="1fr auto"
                gap="10px"
                styles="width: 100%"
                mb={policy.repayment_details.policy_type === VendorProgramPolicyType.ADVANCE ? 4 : 0}
              >
                <Text variant="header3" color={lightGray1}>
                  Billing date
                </Text>
                <FlexBox isColumn alignItems="flex-end">
                  <Text variant="header3" color={primaryBlack}>
                    {policy.repayment_details.next_billing_date
                      ? formatReadableDate(policy.repayment_details.next_billing_date)
                      : 'N/A'}
                  </Text>
                  <Text variant="header3" color={primaryBlack} align="right">
                    {policy.repayment_details.next_billing_date
                      ? formatReadableTime(policy.repayment_details.next_billing_date)
                      : 'N/A'}
                  </Text>
                </FlexBox>

                <Text variant="header3" color={lightGray1}>
                  Billing amount
                </Text>
                <Text variant="header3" color={primaryBlack} align="right">
                  {policy.repayment_details.amount_owed_per_period === 'N/A' ||
                  policy.repayment_details.underpayment_from_previous_periods === 'N/A'
                    ? 'N/A'
                    : formatCurrency(
                        (
                          parseFloat(policy.repayment_details.amount_owed_per_period) +
                          parseFloat(policy.repayment_details.underpayment_from_previous_periods)
                        ).toFixed(2)
                      )}
                </Text>
              </Grid>
            </>
          )}

        {policy.repayment_details.policy_type === VendorProgramPolicyType.ADVANCE && (
          <>
            <Text variant="uppercaseLabel2" color={midGray} mb={2}>
              Total Balance
            </Text>
            <Grid columns="1fr auto" gap="10px" styles="width: 100%">
              <Text variant="header3" color={lightGray1}>
                Policy amount
              </Text>
              <Text variant="header3" color={primaryBlack} align="right">
                {policy.repayment_details.total_amount_owed === 'N/A'
                  ? 'N/A'
                  : formatCurrency(policy.repayment_details.total_amount_owed)}
              </Text>

              <Text variant="header3" color={lightGray1}>
                Amount paid
              </Text>
              <Text variant="header3" color={primaryBlack} align="right">
                {policy.repayment_details.amount_paid === 'N/A'
                  ? 'N/A'
                  : formatCurrency(policy.repayment_details.amount_paid)}
              </Text>

              <Text variant="header3" color={lightGray1}>
                Total remaining balance
              </Text>
              <Text variant="header3" color={primaryBlack} align="right">
                {policy.repayment_details.remaining_payment === 'N/A'
                  ? 'N/A'
                  : formatCurrency(policy.repayment_details.remaining_payment)}
              </Text>
            </Grid>
          </>
        )}
      </div>

      <div>
        <Text variant="uppercaseLabel2" color={midGray} mb={2}>
          Policy details
        </Text>
        <Grid columns="1fr auto" gap="10px" styles="width: 100%">
          <Text variant="header3" color={lightGray1}>
            Start date
          </Text>
          <FlexBox isColumn alignItems="flex-end">
            <Text variant="header3" color={primaryBlack} align="right">
              {formatReadableDate(policy.repayment_details.payment_start_date)}
            </Text>
            <Text variant="header3" color={primaryBlack} align="right">
              {formatReadableTime(policy.repayment_details.payment_start_date)}
            </Text>
          </FlexBox>

          <Text variant="header3" color={lightGray1}>
            Payment schedule
          </Text>
          <Text variant="header3" color={primaryBlack} align="right">
            ({RepaymentScheduleTypeText[schedule]})
          </Text>

          {policy.repayment_details.policy_type === VendorProgramPolicyType.ADVANCE ? (
            <>
              <Text variant="header3" color={lightGray1}>
                Billing period
              </Text>
              <Text variant="header3" color={primaryBlack} align="right">
                {paymentPeriods}
              </Text>
            </>
          ) : (
            <>
              <Text variant="header3" color={lightGray1}>
                {schedule === RepaymentScheduleType.MONTHLY ? 'Monthly' : 'Weekly'} deduction
              </Text>
              <Text variant="header3" color={primaryBlack} align="right">
                {formatCurrency(amountOwedPerPeriod)}
              </Text>
            </>
          )}
        </Grid>
        <Divider mt={2} mb={2} />
        <Grid columns="1fr auto" gap="10px" styles="width: 100%">
          <Text variant="header3" color={lightGray1}>
            Debt write-off
          </Text>
          <Text variant="header3" color={primaryBlack} align="right">
            {formatCurrency(debt_written_off)}
          </Text>
        </Grid>
      </div>
    </Grid>
  )
}

const InsetText = styled(Text)<{ $isLast?: boolean }>`
  padding-left: 15px;
  position: relative;

  &::before,
  &::after {
    position: absolute;
    content: ' ';
    left: 2px;
  }

  &::before {
    border-left: 2px solid ${dividerGray};
    height: 30px;
    bottom: 0;
  }

  &::after {
    border-top: 2px solid ${dividerGray};
    width: 8px;
    bottom: 12px;
  }

  ${(p) =>
    p.$isLast &&
    css`
      &::before {
        height: 25px;
        bottom: 12px;
      }
    `}
`
